import {
	CharacterImageVariant,
	ImageContainerShape,
	renderCharacterImage,
} from '@datapad/common-controls';
import { type PresenceUser, useAppContext } from '@datapad/data-model';
import {
	AccountCircle,
	Event,
	LocalLibrary,
	Map,
	People,
	Person,
	Security,
	SupervisedUserCircle,
	Timeline,
} from '@mui/icons-material';
import {
	Avatar,
	Box,
	Divider,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type React from 'react';
import { AppId } from '../../AppId';
import { usePresence } from '../../hooks/index.js';
import { PresenceTooltip, useResolvedPresenceUser } from '../PresenceUtilities.js';
import {
	menuItemBorderRadius,
	menuLabelSx,
	menuLabelTypographyVariant,
	menuSectionSx,
} from './Constants';

/**
 * {@link AppSelectionList} input props.
 */
export interface AppSelectionListProps {
	// TODO: take permission level, rather than separate `isGuest` and `isGameMaster` props
	/**
	 * Whether the current user is a guest.
	 */
	isGuest: boolean;

	/**
	 * Whether the current user is a game master.
	 */
	isGameMaster: boolean;

	/**
	 * The currently selected app.
	 */
	currentAppSelection: AppId;

	/**
	 * Callback to change the app selection.
	 */
	changeAppSelection(newAppSelection: AppId): void;
}

/**
 * Datapad menu app selection list.
 *
 * @remarks
 * The app list uses:
 * - Rounded selection indicators for a modern feel
 * - Subtle hover effects with theme-aware colors
 * - Grouped sections for regular and admin apps
 */
export function AppSelectionList(props: AppSelectionListProps): React.ReactElement {
	const { isGuest, isGameMaster, currentAppSelection, changeAppSelection } = props;

	const remoteUsers = usePresence();

	/**
	 * Groups remote users by their current app.
	 */
	function usersForApp(appId: AppId): PresenceUser[] {
		const appName = AppId[appId];
		return remoteUsers.filter((user) => user.value.currentApp === appName);
	}

	/**
	 * Generates a React key for the list entry.
	 */
	function appEntryKey(appId: AppId): string {
		return `datapad-menu-app-selection-${appId}`;
	}

	return (
		<Box sx={menuSectionSx}>
			<Typography variant={menuLabelTypographyVariant} sx={menuLabelSx}>
				Apps
			</Typography>
			{/*
			 * MUI List with disablePadding removes default padding,
			 * allowing our custom spacing to control the layout.
			 */}
			<List disablePadding>
				<AppMenuItem
					text="My Profile"
					icon={<AccountCircle />}
					appId={AppId.Profile}
					isCurrentSelection={currentAppSelection === AppId.Profile}
					disabled={isGuest}
					onSelected={() => changeAppSelection(AppId.Profile)}
					activeUsers={usersForApp(AppId.Profile)}
					key={appEntryKey(AppId.Profile)}
				/>
				<AppMenuItem
					text="Galaxy Map"
					icon={<Map />}
					appId={AppId.GalaxyMap}
					isCurrentSelection={currentAppSelection === AppId.GalaxyMap}
					onSelected={() => changeAppSelection(AppId.GalaxyMap)}
					activeUsers={usersForApp(AppId.GalaxyMap)}
					key={appEntryKey(AppId.GalaxyMap)}
				/>
				<AppMenuItem
					text="Contacts"
					icon={<People />}
					appId={AppId.Contacts}
					isCurrentSelection={currentAppSelection === AppId.Contacts}
					onSelected={() => changeAppSelection(AppId.Contacts)}
					activeUsers={usersForApp(AppId.Contacts)}
					key={appEntryKey(AppId.Contacts)}
				/>
				<AppMenuItem
					text="Calendar"
					icon={<Event />}
					appId={AppId.Calendar}
					isCurrentSelection={currentAppSelection === AppId.Calendar}
					onSelected={() => changeAppSelection(AppId.Calendar)}
					activeUsers={usersForApp(AppId.Calendar)}
					key={appEntryKey(AppId.Calendar)}
				/>
				<AppMenuItem
					text="Timeline"
					icon={<Timeline />}
					appId={AppId.Timeline}
					isCurrentSelection={currentAppSelection === AppId.Timeline}
					onSelected={() => changeAppSelection(AppId.Timeline)}
					activeUsers={usersForApp(AppId.Timeline)}
					key={appEntryKey(AppId.Timeline)}
				/>
			</List>

			{/* Game Master administration section */}
			{isGameMaster && (
				<Box sx={{ mt: 2 }}>
					<Typography variant={menuLabelTypographyVariant} sx={menuLabelSx}>
						Administration
					</Typography>
					<List disablePadding>
						<AppMenuItem
							text="Players"
							icon={<SupervisedUserCircle />}
							appId={AppId.Players}
							isCurrentSelection={currentAppSelection === AppId.Players}
							onSelected={() => changeAppSelection(AppId.Players)}
							activeUsers={usersForApp(AppId.Players)}
							key={appEntryKey(AppId.Players)}
						/>
						<AppMenuItem
							text="Campaigns"
							icon={<LocalLibrary />}
							appId={AppId.Campaigns}
							isCurrentSelection={currentAppSelection === AppId.Campaigns}
							onSelected={() => changeAppSelection(AppId.Campaigns)}
							activeUsers={usersForApp(AppId.Campaigns)}
							key={appEntryKey(AppId.Campaigns)}
						/>
					</List>
				</Box>
			)}
		</Box>
	);
}

/**
 * {@link AppMenuItem} input props.
 */
export interface AppMenuItemProps {
	/**
	 * Text to display.
	 */
	text: string;

	/**
	 * Icon to display.
	 */
	icon: React.ReactElement;

	/**
	 * The App ID with which this option corresponds.
	 */
	appId: AppId;

	/**
	 * Whether or not this item is the current app selection.
	 */
	isCurrentSelection: boolean;

	/**
	 * Whether or not the menu option is visible / clickible by the user.
	 *
	 * @defaultValue `false`
	 */
	disabled?: boolean;

	/**
	 * Callback invoked when the item is selected.
	 */
	onSelected: () => void;

	/**
	 * Remote users currently viewing this app.
	 *
	 * @defaultValue `[]`
	 */
	activeUsers?: PresenceUser[];
}

/**
 * Size (in px) for the mini presence avatars shown on menu items.
 */
const miniAvatarSize = 25;

/**
 * Gap (in px) between adjacent mini avatars when the container has enough room.
 */
const miniAvatarGap = 3;

/**
 * Natural width (in px) of each avatar slot (avatar + gap).
 */
const miniSlotWidth = miniAvatarSize + miniAvatarGap;

/**
 * Minimum width (in px) each avatar slot can shrink to, allowing up to 4px overlap.
 */
const miniMinSlotWidth = miniAvatarSize - 4;

/**
 * Maximum number of avatars to display before collapsing to a counter.
 */
const maxVisibleAvatars = 4;

/**
 * A single menu option item for the app selection list.
 *
 * @remarks
 * Uses custom styling for a modern appearance:
 * - Rounded corners for a softer look
 * - Subtle hover background with theme-aware alpha
 * - Selected state uses primary color with transparency
 * - Smooth transitions for hover effects
 */
export function AppMenuItem(props: AppMenuItemProps): React.ReactElement {
	const { text, icon, appId, isCurrentSelection, disabled, onSelected, activeUsers = [] } = props;
	return (
		<ListItemButton
			selected={isCurrentSelection}
			onClick={onSelected}
			key={appId}
			disabled={disabled}
			sx={(theme) => ({
				/*
				 * Rounded corners create a modern, pill-like appearance
				 * for selected items and hover states.
				 */
				borderRadius: menuItemBorderRadius,
				mb: 0.5,
				/*
				 * MUI Pattern: Custom selected state styling
				 *
				 * Override the default selected background with a theme-aware
				 * primary color at low opacity for a subtle highlight.
				 */
				'&.Mui-selected': {
					bgcolor: alpha(theme.palette.primary.main, 0.12),
					'&:hover': {
						bgcolor: alpha(theme.palette.primary.main, 0.18),
					},
				},
				/*
				 * Smooth transition for hover effects creates a polished feel.
				 */
				transition: theme.transitions.create(['background-color'], {
					duration: theme.transitions.duration.short,
				}),
			})}
		>
			{/*
			 * Icon color matches primary when selected for visual consistency.
			 */}
			<ListItemIcon
				sx={(theme) => ({
					color: isCurrentSelection ? theme.palette.primary.main : 'inherit',
					minWidth: 40,
				})}
			>
				{icon}
			</ListItemIcon>
			<ListItemText
				primary={text}
				primaryTypographyProps={{
					fontWeight: isCurrentSelection ? 500 : 400,
					noWrap: true,
				}}
			/>
			{activeUsers.length > 0 &&
				(activeUsers.length <= maxVisibleAvatars ? (
					<Stack
						direction="row"
						sx={{
							ml: 1,
							position: 'relative',
							width: (activeUsers.length - 1) * miniSlotWidth + miniAvatarSize,
						}}
					>
						{activeUsers.map((user, i) => (
							<MiniAvatarWrapper
								key={user.attendee.attendeeId}
								index={i}
								total={activeUsers.length}
							>
								<MiniPresenceAvatar user={user} />
							</MiniAvatarWrapper>
						))}
					</Stack>
				) : (
					<Box sx={{ ml: 1 }}>
						<MiniCollapsedIndicator users={activeUsers} />
					</Box>
				))}
		</ListItemButton>
	);
}

/**
 * Wrapper that positions a mini avatar within the menu facepile stack.
 *
 * @remarks
 * Each wrapper acts as a flex slot that starts at {@link miniSlotWidth} wide
 * but can shrink down to {@link miniMinSlotWidth}. When the slots shrink,
 * the fixed-size avatars overlap. Earlier avatars get a higher z-index so
 * they sit on top; hovering pops the avatar above all others.
 */
function MiniAvatarWrapper(
	props: React.PropsWithChildren<{ index: number; total: number }>,
): React.ReactElement {
	const { children, index, total } = props;
	return (
		<Box
			sx={{
				position: 'relative',
				zIndex: total - index,
				flex: `0 1 ${miniSlotWidth}px`,
				minWidth: `${miniMinSlotWidth}px`,
				height: miniAvatarSize,
				transition: 'transform 0.15s ease',
				'&:hover': {
					zIndex: total + 1,
					transform: 'scale(1.15)',
				},
			}}
		>
			<Box sx={{ position: 'absolute', left: 0, top: 0 }}>{children}</Box>
		</Box>
	);
}

/**
 * Collapsed presence counter shown when there are more than {@link maxVisibleAvatars}
 * users on the same app. Displays a small circle with the total count.
 */
function MiniCollapsedIndicator(props: { users: PresenceUser[] }): React.ReactElement {
	const { users } = props;

	return (
		<Tooltip
			title={
				<Stack spacing={0.5} divider={<Divider flexItem />}>
					{users.map((user) => (
						<MiniPresenceTooltipContent key={user.attendee.attendeeId} user={user} />
					))}
				</Stack>
			}
			disableInteractive={false}
			leaveDelay={150}
		>
			<Box
				data-testid="menu-presence-collapsed"
				sx={{
					width: miniAvatarSize,
					height: miniAvatarSize,
					borderRadius: '50%',
					bgcolor: 'grey.600',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					cursor: 'default',
					flexShrink: 0,
				}}
			>
				<Typography
					sx={{
						fontSize: 11,
						fontWeight: 'bold',
						color: 'text.primary',
						lineHeight: 1,
					}}
				>
					{users.length}
				</Typography>
			</Box>
		</Tooltip>
	);
}

/**
 * Resolves a presence user and renders the appropriate {@link PresenceTooltip} content.
 *
 * @remarks
 * Used by both {@link MiniPresenceAvatar} (individual tooltip) and
 * {@link MiniCollapsedIndicator} (stacked vertical list) so that tooltip
 * content is defined in a single place.
 */
function MiniPresenceTooltipContent(props: { user: PresenceUser }): React.ReactElement {
	const { user } = props;
	const { matchedUser, character, isGameMaster } = useResolvedPresenceUser(user);
	const appContext = useAppContext();
	const { currentApp } = user.value;

	if (matchedUser && isGameMaster) {
		return <PresenceTooltip icon={<Security sx={{ fontSize: 12 }} />} label="Game Master" />;
	}

	if (matchedUser && character) {
		const characterImage = renderCharacterImage(character, {
			variant: CharacterImageVariant.Profile,
			containerShape: ImageContainerShape.Circle,
			maxHeightInPixels: miniAvatarSize,
			maxWidthInPixels: miniAvatarSize,
		});
		return (
			<PresenceTooltip
				icon={characterImage}
				label={character.name}
				subtitle={currentApp}
				onClick={() => appContext.handleCharacterLink(character)}
			/>
		);
	}

	return (
		<PresenceTooltip
			icon={<Person sx={{ fontSize: 12 }} />}
			label="Guest"
			subtitle={currentApp}
		/>
	);
}

/**
 * Renders a tiny presence avatar for a remote user.
 *
 * @remarks
 * Mirrors the resolution logic from {@link PresenceBubble}:
 * 1. Game Master → Security icon
 * 2. Player with a session character → character image
 * 3. Guest/unknown → Person icon
 */
function MiniPresenceAvatar(props: { user: PresenceUser }): React.ReactElement {
	const { user } = props;
	const { matchedUser, character, isGameMaster } = useResolvedPresenceUser(user);

	// Game Master
	if (matchedUser && isGameMaster) {
		return (
			<Tooltip title={<MiniPresenceTooltipContent user={user} />}>
				<Avatar
					data-testid="menu-presence-gm"
					sx={{
						width: miniAvatarSize,
						height: miniAvatarSize,
						bgcolor: 'warning.dark',
						fontSize: 12,
					}}
				>
					<Security sx={{ fontSize: 12 }} />
				</Avatar>
			</Tooltip>
		);
	}

	// Player with a character
	if (matchedUser && character) {
		const characterImage = renderCharacterImage(character, {
			variant: CharacterImageVariant.Profile,
			containerShape: ImageContainerShape.Circle,
			maxHeightInPixels: miniAvatarSize,
			maxWidthInPixels: miniAvatarSize,
		});

		return (
			<Tooltip title={<MiniPresenceTooltipContent user={user} />}>
				<Box
					data-testid="menu-presence-character"
					sx={{
						width: miniAvatarSize,
						height: miniAvatarSize,
						borderRadius: '50%',
						overflow: 'hidden',
						flexShrink: 0,
					}}
				>
					{characterImage}
				</Box>
			</Tooltip>
		);
	}

	// Guest / unknown user
	return (
		<Tooltip title={<MiniPresenceTooltipContent user={user} />}>
			<Avatar
				data-testid="menu-presence-guest"
				sx={{
					width: miniAvatarSize,
					height: miniAvatarSize,
					bgcolor: 'grey.600',
					fontSize: 12,
				}}
			>
				<Person sx={{ fontSize: 12 }} />
			</Avatar>
		</Tooltip>
	);
}
